import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  forwardRef,
  ViewChild,
  AfterViewInit,
  Output,
  EventEmitter
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { takeWhile } from 'rxjs/operators';
import {
  StripeCardComponent,
  StripeFactoryService,
  StripeInstance
} from 'ngx-stripe';
import {
  StripeCardElementOptions,
  StripeElementsOptions,
  Token
} from '@stripe/stripe-js';
const STRIPE_PUBLIC_KEY =
  'pk_live_51JcjAfKeCoUxIkZxkVyA8bUSLht9agUCzQbS78Tapcgb81Gre11bC1GdnmLEMSClGrxoJiSHJiWfWQmVbOzD3su600St5gCLrz';
@Component({
  selector: 'credit-card',
  templateUrl: './credit-card.component.html',
  styleUrls: ['./credit-card.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CreditCardComponent),
      multi: true
    }
  ]
})
export class CreditCardComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
  constructor(
    private fb: FormBuilder,
    private stripeFactory: StripeFactoryService
  ) {}

  @ViewChild(StripeCardComponent) card: StripeCardComponent;
  @Input() key?: string;
  @Output() onBusy = new EventEmitter();
  @Output() onReady = new EventEmitter();

  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#31325F',
        fontWeight: '300',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: '14px',
        '::placeholder': {
          color: '#CFD7E0'
        }
      }
    }
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'en'
  };

  FG = this.fb.group({
    name: [null, [Validators.required]]
  });

  stripe: StripeInstance;
  stripeError: string;
  active = true;
  loading = false;

  get isTouched() {
    return this.FG.get('name').touched;
  }

  get isDirty() {
    return this.FG.get('name').dirty;
  }

  ngOnInit(): void {
    this.stripe = this.stripeFactory.create(this.key || STRIPE_PUBLIC_KEY);
  }

  ngAfterViewInit(): void {
    this.card.blur.pipe(takeWhile(() => this.active)).subscribe(() => {
      this.validateCreditCardAndGetToken();
    });
  }
  ngOnDestroy() {
    this.active = false;
  }

  async validateCreditCardAndGetToken(): Promise<Token> {
    // validations
    if (this.FG.invalid) {
      for (const key in this.FG.controls) {
        if (Object.prototype.hasOwnProperty.call(this.FG.controls, key)) {
          this.FG.controls[key].markAsDirty();
          this.FG.controls[key].updateValueAndValidity();
        }
      }
      return null;
    }

    // create a stripe token to charge the customer with it
    const name = this.FG.get('name').value;
    this.loading = true;
    this.onBusy.emit();
    const result = await this.stripe
      .createToken(this.card.element, { name })
      .toPromise();

    this.loading = false;
    this.onReady.emit();
    if (result.token) {
      // Use the token
      this.setValue(result.token);
    } else if (result.error) {
      // Error creating the token
      this.stripeError = result.error.message;
      this.setValue(null);
    }
  }

  resetStripeToken() {
    this.card?.element?.clear();
    this.FG.reset();
    this.setValue(null);
  }
  /* -------------------------------------------------------------------------- */
  /*                       Control Value Accessor methods                       */
  /* -------------------------------------------------------------------------- */

  val: Token = null;
  onChange: (v: Token) => void;
  onTouch: (v: Token) => void;

  setValue(v: Token) {
    // this value is updated by programmatic changes
    this.val = v;
    this.onChange && this.onChange(v);

    // register touched when all controls are touched
    this.isTouched && this.onTouch && this.onTouch(v);
  }

  // this method sets the value programmatically
  writeValue(value: any) {
    if (!value) {
      this.setValue(null);
      return;
    }

    this.setValue(value);
  }

  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  // upon touching the element, this method gets triggered
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.FG.disable();
    } else {
      this.FG.enable();
    }
  }

  reset() {
    this.setValue(null);
  }
}
